<template>
    <b-modal
        :visible="visible"
        size="xl"
        title="Импорт из Google Контактов"
        scrollable
        hide-footer
        modal-class="workforce-viewport-modal"
        :no-close-on-backdrop="applying"
        :no-close-on-esc="applying"
        @hidden="close"
    >
        <div v-if="loading" class="employee-google-import__state">
            <b-spinner small class="mr-2"></b-spinner>
            Проверяю файл и сопоставляю карточки
        </div>
        <b-alert v-if="errorMessage" show variant="danger">
            {{ errorMessage }}
        </b-alert>
        <section v-if="applyResult && !loading" class="employee-google-import__result">
            <div class="employee-google-import__result-icon">
                <i class="fas fa-check" aria-hidden="true"></i>
            </div>
            <div>
                <h3>Импорт завершён</h3>
                <p>
                    Данные из Google Контактов применены.
                    Пакет импорта №{{ applyResult.batch_id || '—' }}.
                </p>
                <div class="employee-google-import__summary mb-3">
                    <span>Обработано: <strong>{{ applySummary.total }}</strong></span>
                    <span>Дополнено карточек: <strong>{{ applySummary.matched }}</strong></span>
                    <span>Создано карточек: <strong>{{ applySummary.created }}</strong></span>
                    <span>Подтверждено действующих: <strong>{{ applySummary.confirmed }}</strong></span>
                    <span>Пропущено: <strong>{{ applySummary.ignored }}</strong></span>
                </div>
                <b-button variant="primary" @click="close">
                    Закрыть
                </b-button>
            </div>
        </section>
        <template v-else-if="preview && !loading">
            <b-alert show variant="info" class="py-2">
                Это предпросмотр. По ФИО показываются подсказки, но
                автоматическая привязка выполняется только по телефону или email.
                Новая карточка создаётся лишь после явного выбора в строке.
            </b-alert>

            <div class="employee-google-import__summary">
                <span>Всего: <strong>{{ preview.summary.total }}</strong></span>
                <span>Сопоставлены: <strong>{{ preview.summary.matched }}</strong></span>
                <span>Конфликты: <strong>{{ preview.summary.conflict }}</strong></span>
                <span>Без совпадения: <strong>{{ preview.summary.unmatched }}</strong></span>
                <span>Дубликаты: <strong>{{ preview.summary.duplicate_source }}</strong></span>
            </div>

            <div class="employee-google-import__table-wrap">
                <table class="table table-sm table-bordered employee-google-import__table">
                    <thead>
                        <tr>
                            <th>Контакт Google</th>
                            <th>Проверка</th>
                            <th>Решение</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="row in preview.rows" :key="row.decision_key">
                            <td>
                                <strong>{{ row.source_display_name || 'Без имени' }}</strong>
                                <div class="small text-muted">
                                    {{ contactSummary(row) }}
                                </div>
                                <div
                                    v-for="issue in row.issues"
                                    :key="issue.code"
                                    class="small text-muted mt-1"
                                >
                                    {{ issue.message }}
                                </div>
                            </td>
                            <td>
                                <b-badge :variant="statusVariant(row.status)">
                                    {{ statusLabel(row.status) }}
                                </b-badge>
                                <div v-if="row.matched_employee" class="small mt-1">
                                    #{{ row.matched_employee.employee_id }}
                                    {{ row.matched_employee.full_name }}
                                </div>
                                <employee-match-candidates :candidates="row.match_candidates || row.name_candidates || []"
                                    v-model="decisions[row.decision_key].reject_candidate_ids"
                                    :selected-id="decisions[row.decision_key].employee && decisions[row.decision_key].employee.employee_id"
                                    :editable="row.status !== 'duplicate_source' && decisions[row.decision_key].action !== 'ignore'" :disabled="applying" />
                            </td>
                            <td class="employee-google-import__decision">
                                <b-form-select
                                    v-model="decisions[row.decision_key].action"
                                    :options="actionOptions(row)"
                                    size="sm"
                                    @change="onActionChanged(row)"
                                ></b-form-select>
                                <v-select
                                    v-if="decisionRequiresEmployee(decisions[row.decision_key])"
                                    v-model="decisions[row.decision_key].employee"
                                    :options="employeeOptionsForDecision(decisions[row.decision_key])"
                                    label="label"
                                    :reduce="option => option"
                                    :clearable="false"
                                    class="mt-2"
                                    placeholder="Найти сотрудника"
                                >
                                    <template v-slot:option="option">
                                        <div>
                                            <strong>{{ option.label }}</strong>
                                            <small class="d-block text-muted">
                                                #{{ option.employee_id }} · {{ statusLabel(option.lifecycle_status) }}
                                            </small>
                                        </div>
                                    </template>
                                </v-select>
                                <router-link v-if="decisions[row.decision_key].employee"
                                    :to="{name: 'employee-details', params: {id: decisions[row.decision_key].employee.employee_id}}" target="_blank" rel="noopener noreferrer" class="small d-block mt-2">
                                    Открыть выбранную карточку ↗
                                </router-link>
                                <small
                                    v-if="decisionConfirmsActive(decisions[row.decision_key])"
                                    class="d-block mt-2 text-success"
                                >
                                    Вы подтверждаете, что сотрудник действующий. Решение будет записано в историю.
                                </small>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <b-form-checkbox v-model="confirmed" class="mt-3">
                Я проверил сопоставления и подтверждаю применение импорта.
            </b-form-checkbox>
            <div class="d-flex justify-content-end mt-3">
                <b-button
                    variant="outline-secondary"
                    class="mr-2"
                    :disabled="applying"
                    @click="close"
                >
                    Закрыть
                </b-button>
                <b-button
                    variant="success"
                    :disabled="!confirmed || applying || hasIncompleteDecisions"
                    @click="applyImport"
                >
                    <b-spinner v-if="applying" small class="mr-1"></b-spinner>
                    Применить
                </b-button>
            </div>
        </template>
    </b-modal>
</template>

<script>
import api from '@/utils/api';
import EmployeeMatchCandidates from './EmployeeMatchCandidates.vue';

export default {
    name: 'GoogleContactsImportModal',
    components: {EmployeeMatchCandidates},
    props: {
        visible: {
            type: Boolean,
            default: false
        },
        file: {
            type: File,
            default: null
        },
        employees: {
            type: Array,
            default: () => []
        },
        preferredEmployeeId: {
            type: [Number, String],
            default: null
        }
    },
    data() {
        return {
            preview: null,
            applyResult: null,
            decisions: {},
            loading: false,
            applying: false,
            confirmed: false,
            errorMessage: ''
        };
    },
    computed: {
        applySummary() {
            const summary = this.applyResult && this.applyResult.summary || {};
            return {
                total: Number(summary.total || 0),
                matched: Number(summary.matched || 0),
                created: Number(summary.created || 0),
                confirmed: Number(summary.confirmed || 0),
                ignored: Number(summary.ignored || 0)
            };
        },
        employeeOptions() {
            return this.employees.map(employee => ({
                employee_id: Number(employee.employee_id),
                label: employee.full_name || employee.display_name,
                lifecycle_status: employee.lifecycle_status
            })).sort((left, right) => (
                String(left.label || '').localeCompare(
                    String(right.label || ''),
                    'ru'
                )
            ));
        },
        hasIncompleteDecisions() {
            if (!this.preview) return true;
            return this.preview.rows.some(row => {
                const decision = this.decisions[row.decision_key];
                return !decision
                    || !decision.action
                    || (
                        this.decisionRequiresEmployee(decision)
                        && !(decision.employee && decision.employee.employee_id)
                    );
            });
        }
    },
    watch: {
        visible(value) {
            if (value) this.loadPreview();
        },
        file() {
            if (this.visible) this.loadPreview();
        }
    },
    methods: {
        async loadPreview() {
            if (!this.file || this.loading) return;
            this.loading = true;
            this.preview = null;
            this.applyResult = null;
            this.decisions = {};
            this.confirmed = false;
            this.errorMessage = '';
            try {
                const form = new FormData();
                form.append('file', this.file);
                const response = await api.post(
                    '/api/employees/imports/google-contacts/preview',
                    form,
                    {
                        headers: {'Content-Type': 'multipart/form-data'},
                        ignoreModal: true
                    }
                );
                this.preview = response && response.data || null;
                (this.preview && this.preview.rows || []).forEach(row => {
                    const matched = row.matched_employee
                        ? this.employeeOptions.find(option => (
                            Number(option.employee_id)
                                === Number(row.matched_employee.employee_id)
                        ))
                        : null;
                    const preferred = row.status === 'unmatched'
                        ? this.employeeOptions.find(option => (
                            Number(option.employee_id)
                                === Number(this.preferredEmployeeId)
                        ))
                        : null;
                    const selected = matched || preferred || null;
                    this.$set(this.decisions, row.decision_key, {
                        action: selected ? 'match' : 'ignore',
                        reject_candidate_ids: [],
                        employee: selected
                    });
                });
            } catch (error) {
                this.errorMessage = this.errorText(
                    error,
                    'Не удалось проверить файл Google Контактов.'
                );
            } finally {
                this.loading = false;
            }
        },
        actionOptions(row) {
            const result = [
                {value: 'ignore', text: 'Не импортировать'},
                {value: 'match', text: 'Привязать без изменения статуса'},
                {
                    value: 'match_active',
                    text: 'Привязать и подтвердить как действующего'
                }
            ];
            if (row.status === 'unmatched') {
                result.push({
                    value: 'create',
                    text: row.match_candidates && row.match_candidates.some(candidate => candidate.match_decision !== 'reject')
                        ? 'Никто из списка — создать нового и запомнить' : 'Создать как непроверенного'
                });
                result.push({
                    value: 'create_active',
                    text: row.match_candidates && row.match_candidates.some(candidate => candidate.match_decision !== 'reject')
                        ? 'Никто из списка — создать действующего и запомнить' : 'Создать как действующего — подтверждаю сотрудника'
                });
            }
            return result;
        },
        decisionRequiresEmployee(decision) {
            return Boolean(
                decision
                && ['match', 'match_active'].indexOf(decision.action) !== -1
            );
        },
        decisionConfirmsActive(decision) {
            return Boolean(
                decision
                && ['match_active', 'create_active'].indexOf(decision.action) !== -1
            );
        },
        employeeOptionsForDecision(decision) {
            if (decision && decision.action === 'match_active') {
                return this.employeeOptions.filter(option => (
                    option.lifecycle_status === 'unverified'
                ));
            }
            return this.employeeOptions;
        },
        onActionChanged(row) {
            const decision = this.decisions[row.decision_key];
            if (!decision) return;
            if (!this.decisionRequiresEmployee(decision)) {
                decision.employee = null;
                return;
            }
            if (
                decision.action === 'match_active'
                && decision.employee
                && decision.employee.lifecycle_status !== 'unverified'
            ) {
                decision.employee = null;
            }
            if (row.matched_employee && !decision.employee) {
                decision.employee = this.employeeOptionsForDecision(decision).find(option => (
                    Number(option.employee_id)
                        === Number(row.matched_employee.employee_id)
                )) || null;
            }
        },
        async applyImport() {
            if (!this.preview || this.hasIncompleteDecisions || this.applying) {
                return;
            }
            this.applying = true;
            this.errorMessage = '';
            try {
                const decisions = Object.keys(this.decisions).reduce((result, key) => {
                    const decision = this.decisions[key];
                    const reviewed = this.preview.rows.find(row => row.decision_key === key);
                    result[key] = {
                        action: decision.action,
                        reject_candidate_ids: (decision.reject_candidate_ids || []).filter(id => !decision.employee || Number(id) !== Number(decision.employee.employee_id)),
                        reject_all_candidates: ['create', 'create_active'].includes(decision.action) && (reviewed.match_candidates || []).some(candidate => candidate.match_decision !== 'reject'),
                        match_review_token: reviewed.match_review_token,
                        employee_id: decision.employee
                            ? decision.employee.employee_id
                            : null
                    };
                    return result;
                }, {});
                const form = new FormData();
                form.append('file', this.file);
                form.append('file_sha256', this.preview.file_sha256);
                form.append('apply', 'true');
                form.append('decisions', JSON.stringify(decisions));
                const response = await api.post(
                    '/api/employees/imports/google-contacts/apply',
                    form,
                    {
                        headers: {'Content-Type': 'multipart/form-data'},
                        ignoreModal: true
                    }
                );
                const result = response && response.data || null;
                if (!result || result.status !== 'applied' || !result.summary) {
                    throw new Error('Сервер не вернул результат применения импорта.');
                }
                this.applyResult = result;
                this.preview = null;
                this.confirmed = false;
                this.$emit('applied', result);
            } catch (error) {
                this.errorMessage = this.errorText(
                    error,
                    'Не удалось применить импорт.'
                );
            } finally {
                this.applying = false;
            }
        },
        contactSummary(row) {
            const methods = row && row.canonical
                && Array.isArray(row.canonical.contact_methods)
                ? row.canonical.contact_methods
                : [];
            return methods.map(method => method.value).filter(Boolean).join(' · ')
                || 'Контактные данные не указаны';
        },
        statusLabel(status) {
            return {
                active: 'Действующий',
                reserve: 'Резерв',
                unverified: 'Непроверенный',
                matched: 'Надёжное совпадение',
                conflict: 'Конфликт',
                unmatched: 'Без совпадения',
                duplicate_source: 'Дубликат файла'
            }[status] || status || 'Не указан';
        },
        statusVariant(status) {
            if (status === 'matched') return 'success';
            if (status === 'conflict') return 'danger';
            if (status === 'unmatched') return 'warning';
            return 'secondary';
        },
        errorText(error, fallback) {
            const data = error && error.response ? error.response.data : null;
            if (data && typeof data === 'object') {
                return data.msg || data.message || fallback;
            }
            if (typeof data === 'string' && data) {
                return data;
            }
            return error && error.message ? error.message : fallback;
        },
        close() {
            if (this.applying) return;
            this.$emit('close');
        }
    }
};
</script>

<style scoped>
.employee-google-import__state {
    min-height: 180px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #66788a;
}

.employee-google-import__result {
    display: flex;
    align-items: flex-start;
    gap: 16px;
    padding: 18px;
    border: 1px solid #b9dfc7;
    border-radius: 10px;
    background: #f2fbf5;
}

.employee-google-import__result h3 {
    margin: 0 0 6px;
}

.employee-google-import__result p {
    margin-bottom: 14px;
    color: #5e7184;
}

.employee-google-import__result-icon {
    display: flex;
    flex: 0 0 44px;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background: #38a169;
    color: #fff;
    font-size: 1.25rem;
}

.employee-google-import__summary {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 20px;
    padding: 10px 12px;
    margin-bottom: 12px;
    border: 1px solid #dce5ee;
    border-radius: 8px;
    background: #f8fafc;
}

.employee-google-import__table-wrap {
    max-height: 55vh;
    overflow: auto;
}

.employee-google-import__table th {
    position: sticky;
    top: 0;
    z-index: 1;
    background: #f1f5f9;
}

.employee-google-import__decision {
    min-width: 290px;
}
</style>

<style src="./workforce-viewport-modal.css"></style>
